import { camelToSnake, snakeToCamel } from "caseparser";

import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";

export const lessonsApi = api
  .enhanceEndpoints({ addTagTypes: ["lessons", "lessonGroups"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      deleteLesson: builder.mutation({
        query: ({ branchId, id, all }) => ({
          url: `branchs/${branchId}/lessons/${id}?all=${all}`,
          method: RequestMethod.DELETE,
          body: {
            all,
          },
        }),
        invalidatesTags: ["lessons", "lessonGroups"],
      }),
      updateLesson: builder.mutation({
        query: ({
          id,
          branchId,
          userId,
          roomId,
          name,
          startDate,
          endDate,
          startTime,
          days,
          day,
          color,
          capacity,
          expectedAttendance,
          duration,
          all,
          fees,
        }) => ({
          url: `branchs/${branchId}/lessons/${id}`,
          method: RequestMethod.PUT,
          body: camelToSnake({
            id,
            name,
            startDate,
            endDate,
            startTime,
            day,
            days,
            color,
            roomId,
            userId,
            capacity,
            expectedAttendance,
            duration,
            all,
            branchId,
            fees,
          }),
        }),
      }),
      createLesson: builder.mutation({
        query: ({
          branchId,
          userId,
          roomId,
          name,
          startDate,
          endDate,
          startTime,
          days,
          color,
          fees,
          capacity,
          expectedAttendance,
          duration,
          is_force,
        }) => ({
          url: `branchs/${branchId}/lessons`,
          method: RequestMethod.POST,
          body: camelToSnake({
            name,
            startDate,
            endDate,
            startTime,
            days,
            fees,
            color,
            roomId,
            is_force,
            userId,
            capacity,
            expectedAttendance,
            duration,
          }),
        }),
        invalidatesTags: ["lessonGroups"],
      }),
      lessons: builder.query({
        query: ({ branchId, groups, month }) => ({
          url: `branchs/${branchId}/lessons/events`,
          method: RequestMethod.POST,
          body: {
            groups,
            month,
          },
        }),
        transformResponse: (response: any) =>
          response.data.map((lesson: any) =>
            snakeToCamel({
              ...lesson,
              start_date: lesson.start_date,
              end_date: lesson.end_date,
              start_time: lesson.time_start,
              end_time: lesson.time_end,
              fees: lesson.fees?.split(","),
            })
          ),
        providesTags: ["lessons"],
      }),
      lessonGroups: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/lessons/groups`,
          method: RequestMethod.GET,
        }),
        transformResponse: (response: any) => response.data,
        providesTags: ["lessonGroups"],
      }),
      updateLessonsGroup: builder.mutation({
        query: ({ id, branchId, group, description, file, color }) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("group", group);
          formData.append("description", description);
          formData.append("branch_id", branchId);
          formData.append("id", id);
          formData.append("color", color);

          return {
            formData: true,
            url: `branchs/${branchId}/lesson-groups`,
            method: RequestMethod.POST,
            body: formData,
          };
        },
        invalidatesTags: ["lessonGroups"],
      }),
      storeFile: builder.mutation({
        query: ({ id, fileStore }: { id: string; fileStore: FormData }) => ({
          url: `branchs/${id}/lessons/file`,
          method: RequestMethod.POST,
          body: fileStore,
        }),
      }),
      lessonsUser: builder.query({
        query: ({ branchId, userId, month }) => ({
          url: `branchs/${branchId}/lessons/get-list-lesson`,
          method: RequestMethod.POST,
          body: {
            user_id: userId,
            month,
          },
        }),
        transformResponse: (response: any) =>
          response.data.map((lesson: any) =>
            snakeToCamel({
              ...lesson,
              start_date: lesson.start_date,
              end_date: lesson.end_date,
              start_time: lesson.time_start,
              end_time: lesson.time_end,
              fees: lesson.fees?.split(","),
            })
          ),
        providesTags: ["lessons"],
      }),
      enrollLesson: builder.mutation({
        query: ({ lessonId, userId, allEvent }) => ({
          url: `/lesson-user/enroll`,
          method: RequestMethod.POST,
          body: camelToSnake({
            userId,
            lessonId,
            allEvent,
          }),
        }),
        invalidatesTags: ["lessons"],
      }),
      deleteLessonProfessionalService: builder.mutation({
        query: ({ branchId, id, all }) => ({
          url: `/branchs/${branchId}/assisteds/delete-assisted/${id}`,
          method: RequestMethod.POST,
          body: {
            all,
          },
        }),
        invalidatesTags: ["lessons"],
      }),
    }),
  });

export const {
  useLessonsQuery,
  useLessonGroupsQuery,
  useDeleteLessonMutation,
  useUpdateLessonMutation,
  useCreateLessonMutation,
  useUpdateLessonsGroupMutation,
  useLessonsUserQuery,
  useEnrollLessonMutation,
  useStoreFileMutation,
  useDeleteLessonProfessionalServiceMutation,
} = lessonsApi;
